import React, { useEffect, useMemo, useState } from 'react';
import { Inputs, DEFAULT_INPUTS, calculateModel } from '../utils/roiCalculations';
import { ResultsSummary } from '../components/ResultsSummary';
import { ProfitChart } from '../components/ProfitChart';
import { ProjectionTable } from '../components/ProjectionTable';
import { fmtMoney, fmtPct } from '../utils/formatters';
interface ResultsPageProps {
  isPreview?: boolean;
}
export function ResultsPage({
  isPreview = false
}: ResultsPageProps) {
  const [inputs, setInputs] = useState<Inputs>(DEFAULT_INPUTS);
  const [halfSales, setHalfSales] = useState(false);
  const [doubleAttrition, setDoubleAttrition] = useState(false);
  const [showAssumptions, setShowAssumptions] = useState(false);
  useEffect(() => {
    if (isPreview) {
      const loadInputs = () => {
        const stored = localStorage.getItem('previewInputs');
        if (stored) {
          try {
            setInputs(JSON.parse(stored));
          } catch (e) {
            console.error('Failed to parse preview inputs', e);
          }
        }
      };
      loadInputs();
      const interval = setInterval(loadInputs, 500);
      return () => clearInterval(interval);
    } else {
      const stored = localStorage.getItem('calculatorInputs');
      if (stored) {
        try {
          setInputs(JSON.parse(stored));
        } catch (e) {
          console.error('Failed to parse calculator inputs', e);
        }
      }
    }
  }, [isPreview]);
  const handleInputChange = (field: keyof Inputs, value: number) => {
    setInputs({
      ...inputs,
      [field]: value
    });
  };
  const results = useMemo(() => {
    return calculateModel(inputs);
  }, [inputs]);
  const testimonials = [{
    name: 'Sarah Johnson',
    company: 'Apex Solar',
    role: 'VP of Sales',
    quote: 'HIRE transformed our recruiting process. We went from struggling to find quality reps to building a team of 50+ high performers in just 6 months.',
    image: 'https://i.pravatar.cc/150?img=1'
  }, {
    name: 'Michael Chen',
    company: 'Premier Windows',
    role: 'CEO',
    quote: 'The ROI was undeniable. Within 90 days, our new hires were generating revenue that more than covered our investment.',
    image: 'https://i.pravatar.cc/150?img=2'
  }, {
    name: 'Jessica Martinez',
    company: 'GreenScape Landscaping',
    role: 'Director of Operations',
    quote: "We tried Indeed on our own for years with mediocre results. HIRE's system helped us finally crack the code on consistent, quality hiring.",
    image: 'https://i.pravatar.cc/150?img=3'
  }, {
    name: 'David Thompson',
    company: 'Elite Pest Control',
    role: 'Founder',
    quote: 'The retention rates speak for themselves. Our reps stay longer and perform better than ever before.',
    image: 'https://i.pravatar.cc/150?img=4'
  }, {
    name: 'Amanda Lee',
    company: 'Skyline Roofing',
    role: 'Head of Recruiting',
    quote: 'HIRE took the guesswork out of recruiting. Their system is proven, repeatable, and delivers results every single month.',
    image: 'https://i.pravatar.cc/150?img=5'
  }, {
    name: 'Robert Garcia',
    company: 'Bright Energy Solutions',
    role: 'VP of Growth',
    quote: 'We scaled from 10 to 75 sales reps in under a year. HIRE made it possible without sacrificing quality.',
    image: 'https://i.pravatar.cc/150?img=6'
  }];
  const BookCallButton = () => <a href="https://project-hiredeck.magicpatterns.app/" target="_blank" rel="noopener noreferrer" className="inline-block px-6 md:px-8 py-3 md:py-4 bg-amber hover:bg-amber-dark text-navy font-bold text-base md:text-lg rounded-lg transition-all shadow-xl hover:shadow-amber/50 hover:scale-105 active:scale-100" data-id="element-640">
      Book My Free Strategy Call
    </a>;
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };
  return <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white" data-id="element-641">
      {/* Header */}
      <header className="border-b-2 border-gray-200 bg-white" data-id="element-642">
        <div className="max-w-[1400px] mx-auto px-4 py-3 md:py-6 flex items-center justify-center" data-id="element-643">
          <img src="https://cdn.magicpatterns.com/uploads/jrZpZA6KS7cefrnJNRcqNs/Copy_of_HIRE.png" alt="HIRE" className="h-8 md:h-12" data-id="element-644" />
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-4 py-6 md:py-12" data-id="element-645">
        {/* Hero Section */}
        <div id="hero" className="text-center mb-8 md:mb-12" data-id="element-646">
          <h1 className="text-3xl md:text-5xl font-bold text-navy mb-3 md:mb-4" data-id="element-647">
            Your Personalized ROI Projection
          </h1>
          <p className="text-base md:text-xl text-gray-600 mb-6 md:mb-8 max-w-3xl mx-auto" data-id="element-648">
            {inputs.companyName}, here's what your recruiting investment could
            generate over the next 12 months
          </p>
        </div>

        {/* Recruiting Assumptions Toggle */}
        <div className="mb-8 md:mb-12" data-id="element-649">
          <button onClick={() => setShowAssumptions(!showAssumptions)} className="w-full bg-white border-2 border-gray-200 rounded-lg p-4 md:p-6 shadow-lg hover:shadow-xl transition-all flex items-center justify-between" data-id="element-650">
            <div className="flex items-center gap-3" data-id="element-651">
              <svg className="w-5 h-5 md:w-6 md:h-6 text-navy" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-652">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" data-id="element-653" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" data-id="element-654" />
              </svg>
              <div className="text-left" data-id="element-655">
                <h3 className="text-base md:text-lg font-bold text-navy" data-id="element-656">
                  Adjust Recruiting Assumptions
                </h3>
                <p className="text-xs md:text-sm text-gray-600" data-id="element-657">
                  Fine-tune your ad spend, cost per hire, and recruiter rate
                </p>
              </div>
            </div>
            <svg className={`w-5 h-5 md:w-6 md:h-6 text-navy transition-transform ${showAssumptions ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-658">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" data-id="element-659" />
            </svg>
          </button>

          {showAssumptions && <div className="mt-4 bg-white border-2 border-gray-200 rounded-lg p-4 md:p-6 shadow-lg" data-id="element-660">
              <div className="space-y-6" data-id="element-661">
                {/* Monthly Ad Spend */}
                <div data-id="element-662">
                  <div className="flex items-center justify-between mb-3" data-id="element-663">
                    <label className="text-sm font-semibold text-gray-700" data-id="element-664">
                      Monthly Ad Spend (Indeed/Job Boards)
                    </label>
                    <span className="text-lg font-bold text-navy" data-id="element-665">
                      {fmtMoney(inputs.adSpend)}
                    </span>
                  </div>
                  <input type="range" min="500" max="20000" step="500" value={inputs.adSpend} onChange={e => handleInputChange('adSpend', parseFloat(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-amber" style={{
                background: `linear-gradient(to right, #F59E0B 0%, #F59E0B ${(inputs.adSpend - 500) / (20000 - 500) * 100}%, #E5E7EB ${(inputs.adSpend - 500) / (20000 - 500) * 100}%, #E5E7EB 100%)`
              }} data-id="element-666" />
                  <div className="flex justify-between text-xs text-gray-500 mt-1" data-id="element-667">
                    <span data-id="element-668">$500</span>
                    <span data-id="element-669">$20,000</span>
                  </div>
                </div>

                {/* Cost Per Hire */}
                <div data-id="element-670">
                  <div className="flex items-center justify-between mb-3" data-id="element-671">
                    <label className="text-sm font-semibold text-gray-700" data-id="element-672">
                      Cost Per Hire
                    </label>
                    <span className="text-lg font-bold text-navy" data-id="element-673">
                      {fmtMoney(inputs.cph)}
                    </span>
                  </div>
                  <input type="range" min="100" max="1000" step="25" value={inputs.cph} onChange={e => handleInputChange('cph', parseFloat(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-amber" style={{
                background: `linear-gradient(to right, #F59E0B 0%, #F59E0B ${(inputs.cph - 100) / (1000 - 100) * 100}%, #E5E7EB ${(inputs.cph - 100) / (1000 - 100) * 100}%, #E5E7EB 100%)`
              }} data-id="element-674" />
                  <div className="flex justify-between text-xs text-gray-500 mt-1" data-id="element-675">
                    <span data-id="element-676">$100</span>
                    <span data-id="element-677">$1,000</span>
                  </div>
                </div>

                {/* Recruiter Hourly Rate */}
                <div data-id="element-678">
                  <div className="flex items-center justify-between mb-3" data-id="element-679">
                    <label className="text-sm font-semibold text-gray-700" data-id="element-680">
                      Recruiter Hourly Rate
                    </label>
                    <span className="text-lg font-bold text-navy" data-id="element-681">
                      {fmtMoney(inputs.recruiterHourlyRate)}/hr
                    </span>
                  </div>
                  <input type="range" min="15" max="150" step="5" value={inputs.recruiterHourlyRate} onChange={e => handleInputChange('recruiterHourlyRate', parseFloat(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-amber" style={{
                background: `linear-gradient(to right, #F59E0B 0%, #F59E0B ${(inputs.recruiterHourlyRate - 15) / (150 - 15) * 100}%, #E5E7EB ${(inputs.recruiterHourlyRate - 15) / (150 - 15) * 100}%, #E5E7EB 100%)`
              }} data-id="element-682" />
                  <div className="flex justify-between text-xs text-gray-500 mt-1" data-id="element-683">
                    <span data-id="element-684">$15</span>
                    <span data-id="element-685">$150</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-2" data-id="element-686">
                    We estimate 5 hours of recruiting time per hire
                  </p>
                </div>
              </div>
            </div>}
        </div>

        {/* Video Section */}
        <div id="video" className="mb-8 md:mb-12" data-id="element-687">
          <div className="max-w-4xl mx-auto" data-id="element-688">
            <div className="aspect-video bg-gradient-to-br from-navy to-navy-light rounded-lg flex items-center justify-center border-2 border-gray-200 shadow-xl" data-id="element-689">
              <div className="text-center text-white p-6 md:p-8" data-id="element-690">
                <svg className="w-12 h-12 md:w-20 md:h-20 mx-auto mb-3 md:mb-4" fill="currentColor" viewBox="0 0 20 20" data-id="element-691">
                  <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" data-id="element-692" />
                </svg>
                <p className="text-lg md:text-xl font-semibold" data-id="element-693">
                  Explainer Video
                </p>
                <p className="text-blue-200 mt-2 text-sm md:text-base" data-id="element-694">
                  *will be replaced with a new VSL*
                </p>
              </div>
            </div>
          </div>
          <div className="flex justify-center mt-6 md:mt-8" data-id="element-695">
            <BookCallButton data-id="element-696" />
          </div>
        </div>

        {/* Results Summary */}
        <div id="summary" className="mb-8 md:mb-12" data-id="element-697">
          <h2 className="text-2xl md:text-3xl font-bold text-navy mb-4 md:mb-6 text-center" data-id="element-698">
            Your Numbers at a Glance
          </h2>
          <ResultsSummary results={results} data-id="element-699" />
          <div className="flex justify-center mt-6 md:mt-8" data-id="element-700">
            <BookCallButton data-id="element-701" />
          </div>
        </div>

        {/* ROI Chart */}
        <div id="roi-chart" className="mb-8 md:mb-12" data-id="element-702">
          <h2 className="text-2xl md:text-3xl font-bold text-navy mb-4 md:mb-6 text-center" data-id="element-703">
            Your 12-Month ROI Trajectory
          </h2>
          <ProfitChart rows={results.rows} breakeven={results.breakeven} halfSales={halfSales} setHalfSales={setHalfSales} doubleAttrition={doubleAttrition} setDoubleAttrition={setDoubleAttrition} data-id="element-704" />
          <div className="flex justify-center mt-6 md:mt-8" data-id="element-705">
            <BookCallButton data-id="element-706" />
          </div>
        </div>

        {/* Detailed Projections */}
        <div id="projections" className="mb-8 md:mb-12" data-id="element-707">
          <h2 className="text-2xl md:text-3xl font-bold text-navy mb-4 md:mb-6 text-center" data-id="element-708">
            Month-by-Month Breakdown
          </h2>
          <ProjectionTable rows={results.rows} inputs={inputs} onInputsChange={setInputs} halfSales={halfSales} setHalfSales={setHalfSales} doubleAttrition={doubleAttrition} setDoubleAttrition={setDoubleAttrition} data-id="element-709" />
          <div className="flex justify-center mt-6 md:mt-8" data-id="element-710">
            <BookCallButton data-id="element-711" />
          </div>
        </div>

        {/* Key Insights */}
        <div id="insights" className="mb-8 md:mb-12" data-id="element-712">
          <h2 className="text-2xl md:text-3xl font-bold text-navy mb-4 md:mb-6 text-center" data-id="element-713">
            Key Insights
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6" data-id="element-714">
            <div className="bg-white border-2 border-gray-200 rounded-lg p-4 md:p-6 shadow-lg" data-id="element-715">
              <div className="text-3xl md:text-4xl font-bold text-amber mb-2" data-id="element-716">
                {results.breakeven}
              </div>
              <div className="text-xs md:text-sm font-semibold text-gray-600" data-id="element-717">
                Break-Even Point
              </div>
              <p className="text-xs md:text-sm text-gray-500 mt-2" data-id="element-718">
                When your revenue exceeds your total investment
              </p>
            </div>
            <div className="bg-white border-2 border-gray-200 rounded-lg p-4 md:p-6 shadow-lg" data-id="element-719">
              <div className="text-3xl md:text-4xl font-bold text-green-600" data-id="element-720">
                {fmtPct(results.roi)}
              </div>
              <div className="text-xs md:text-sm font-semibold text-gray-600" data-id="element-721">
                12-Month ROI
              </div>
              <p className="text-xs md:text-sm text-gray-500 mt-2" data-id="element-722">
                Return on your recruiting investment
              </p>
            </div>
            <div className="bg-white border-2 border-gray-200 rounded-lg p-4 md:p-6 shadow-lg" data-id="element-723">
              <div className="text-3xl md:text-4xl font-bold text-navy" data-id="element-724">
                {results.hiresPerMonth.toFixed(0)}
              </div>
              <div className="text-xs md:text-sm font-semibold text-gray-600" data-id="element-725">
                Hires Per Month
              </div>
              <p className="text-xs md:text-sm text-gray-500 mt-2" data-id="element-726">
                Consistent, predictable hiring volume
              </p>
            </div>
          </div>
          <div className="flex justify-center mt-6 md:mt-8" data-id="element-727">
            <BookCallButton data-id="element-728" />
          </div>
        </div>

        {/* Testimonials */}
        <div id="testimonials" className="mb-8 md:mb-12" data-id="element-729">
          <h2 className="text-2xl md:text-3xl font-bold text-navy mb-4 md:mb-6 text-center" data-id="element-730">
            What Our Clients Say
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6" data-id="element-731">
            {testimonials.map((testimonial, index) => <div key={index} className="bg-white border-2 border-gray-200 rounded-lg p-4 md:p-6 shadow-lg" data-id="element-732">
                <div className="flex items-center gap-3 md:gap-4 mb-3 md:mb-4" data-id="element-733">
                  <img src={testimonial.image} alt={testimonial.name} className="w-10 h-10 md:w-12 md:h-12 rounded-full" data-id="element-734" />
                  <div data-id="element-735">
                    <div className="font-bold text-navy text-sm md:text-base" data-id="element-736">
                      {testimonial.name}
                    </div>
                    <div className="text-xs md:text-sm text-gray-600" data-id="element-737">
                      {testimonial.role}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-738">
                      {testimonial.company}
                    </div>
                  </div>
                </div>
                <p className="text-sm md:text-base text-gray-700 italic" data-id="element-739">
                  "{testimonial.quote}"
                </p>
              </div>)}
          </div>
        </div>

        {/* Final CTA */}
        <div className="bg-gradient-to-br from-navy to-navy-light rounded-lg p-6 md:p-12 text-center text-white shadow-xl mb-8 md:mb-12" data-id="element-740">
          <h2 className="text-2xl md:text-4xl font-bold mb-3 md:mb-4" data-id="element-741">
            Ready to Achieve These Results?
          </h2>
          <p className="text-base md:text-xl text-blue-100 mb-6 md:mb-8 max-w-2xl mx-auto" data-id="element-742">
            Book a free strategy call to discuss how HIRE can help you build a
            high-performing sales team with predictable ROI.
          </p>
          <a href="https://project-hiredeck.magicpatterns.app/" target="_blank" rel="noopener noreferrer" className="inline-block px-6 md:px-8 py-3 md:py-4 bg-amber hover:bg-amber-dark text-navy font-bold text-lg md:text-xl rounded-lg transition-all shadow-xl hover:shadow-amber/50 hover:scale-105 active:scale-100" data-id="element-743">
            Book My Free Strategy Call
          </a>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-navy border-t-2 border-white/10" data-id="element-744">
        <div className="max-w-7xl mx-auto px-4 py-6 md:py-8" data-id="element-745">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 md:gap-6" data-id="element-746">
            <img src="https://cdn.magicpatterns.com/uploads/jrZpZA6KS7cefrnJNRcqNs/Copy_of_HIRE.png" alt="HIRE" className="h-8" data-id="element-747" />
            <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 text-xs md:text-sm text-blue-200" data-id="element-748">
              <button onClick={() => scrollToSection('hero')} className="hover:text-white transition-colors" data-id="element-749">
                Overview
              </button>
              <button onClick={() => scrollToSection('video')} className="hover:text-white transition-colors" data-id="element-750">
                Video
              </button>
              <button onClick={() => scrollToSection('summary')} className="hover:text-white transition-colors" data-id="element-751">
                Summary
              </button>
              <button onClick={() => scrollToSection('roi-chart')} className="hover:text-white transition-colors" data-id="element-752">
                ROI Chart
              </button>
              <button onClick={() => scrollToSection('projections')} className="hover:text-white transition-colors" data-id="element-753">
                Projections
              </button>
              <button onClick={() => scrollToSection('insights')} className="hover:text-white transition-colors" data-id="element-754">
                Insights
              </button>
              <button onClick={() => scrollToSection('testimonials')} className="hover:text-white transition-colors" data-id="element-755">
                Testimonials
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>;
}